import { makeStyles } from "@mui/styles";
import React, { useMemo, useRef, useState } from "react";

const useStyles = makeStyles({
  valueInput: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    "& > input": { flex: 1, minWidth: 0 }
  }
});

const MANUAL_ENTRY = "manual";
const API_KEY_HEADER = "x-api-key";

export interface SelectableApiKey {
  id: string;
  name: string;
  value: string;
  enabled: boolean;
}

type InputProps = React.InputHTMLAttributes<HTMLInputElement>;

// Props swagger-ui passes to its `apiKeyAuth` component (Immutable.js structures)
export interface ApiKeyAuthProps {
  name: string;
  schema: { get: (key: string) => unknown };
  authorized?: { getIn: (path: string[]) => unknown } | null;
  onChange: (state: {
    name: string;
    schema: ApiKeyAuthProps["schema"];
    value: string;
  }) => void;
  getComponent: (
    name: string,
    ...rest: unknown[]
  ) => React.ComponentType<InputProps>;
}

interface ApiKeyAuthSelectProps extends ApiKeyAuthProps {
  Original: React.ComponentType<ApiKeyAuthProps>;
  apiKeys: SelectableApiKey[];
}

/** Selection of an API key, with swagger-ui's value input shown only for entering a key manually. */
function ApiKeyValueInput({
  Input,
  inputProps,
  apiKeys,
  onSelectKey
}: {
  Input: React.ComponentType<InputProps>;
  inputProps: InputProps;
  apiKeys: SelectableApiKey[];
  onSelectKey: (value: string) => void;
}) {
  const classes = useStyles();
  const [choice, setChoice] = useState("");

  return (
    <div className={classes.valueInput}>
      <select
        aria-label="Select an API key"
        value={choice}
        onChange={event => {
          const selected = event.target.value;
          setChoice(selected);
          onSelectKey(
            apiKeys.find(apiKey => apiKey.id === selected)?.value ?? ""
          );
        }}
        data-testid="api-tester-api-key-select"
      >
        <option value="" disabled>
          Select an API key
        </option>
        {apiKeys.map(apiKey => (
          <option key={apiKey.id} value={apiKey.id} disabled={!apiKey.enabled}>
            {apiKey.enabled ? apiKey.name : `${apiKey.name} (disabled)`}
          </option>
        ))}
        <option value={MANUAL_ENTRY}>Enter key manually…</option>
      </select>
      {choice === MANUAL_ENTRY && <Input {...inputProps} autoFocus />}
    </div>
  );
}

/** Extends swagger-ui's API key input in the authorize dialog with a selection of the tenant's API keys. */
export default function ApiKeyAuthSelect({
  Original,
  apiKeys,
  ...props
}: ApiKeyAuthSelectProps) {
  const latestProps = useRef(props);
  latestProps.current = props;
  const { getComponent } = props;

  // Swaps swagger-ui's value input for the selection; memoized so its state survives re-renders
  const getComponentWithSelection = useMemo(() => {
    const Input = getComponent("Input");
    const selectKey = (value: string) => {
      const { name, schema, onChange } = latestProps.current;
      onChange({ name, schema, value });
    };
    const ValueInput = (inputProps: InputProps) => (
      <ApiKeyValueInput
        Input={Input}
        inputProps={inputProps}
        apiKeys={apiKeys}
        onSelectKey={selectKey}
      />
    );
    return (name: string, ...rest: unknown[]) =>
      name === "Input" ? ValueInput : getComponent(name, ...rest);
  }, [getComponent, apiKeys]);

  // Other API key schemes, e.g. the grant token header, keep swagger-ui's plain input
  const isApiKeyHeader =
    props.schema.get("in") === "header" &&
    String(props.schema.get("name")).toLowerCase() === API_KEY_HEADER;
  if (!isApiKeyHeader) return <Original {...props} />;

  return <Original {...props} getComponent={getComponentWithSelection} />;
}
